import { useEffect, useRef, useState } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { track } from '../lib/track';
import { trackEvent } from '../lib/analytics';

interface PaymentSuccessModalProps {
  planName: string;
  onDismiss: () => void;
  campId?: string;
  campSlug?: string;
}

/**
 * Payment Success Modal Component
 *
 * Shows a success message when a user completes payment for a camp claim.
 * Displays the plan name and provides guidance on next steps.
 * Fires purchase_completed when shown so GTM/analytics capture the conversion
 * even if the user landed here via redirect from claim/success.
 */
export default function PaymentSuccessModal({ planName, onDismiss, campId, campSlug }: PaymentSuccessModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Fire purchase_completed once when overlay is shown (in case it didn't fire on claim/success due to redirect)
  useEffect(() => {
    if (isVisible && !purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      track('purchase_completed', {
        persona: 'camp_owner',
        product_type: 'camp_claim',
        plan_name: planName,
        camp_id: campId,
        camp_slug: campSlug,
        source: 'payment_success_modal',
      });
      trackEvent('Camp-Claim-Conversion', {
        plan: planName,
        camp_id: campId,
        camp_slug: campSlug,
      });
    }
  }, [isVisible, planName, campId, campSlug]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for animation to complete before calling onDismiss
    setTimeout(onDismiss, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isVisible ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close success message"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Success content */}
        <div className="text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            🎉 Payment Successful!
          </h2>
          
          <p className="text-lg text-gray-700 mb-4">
            You're now on the <span className="font-semibold text-primary">{planName}</span> plan
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800">
              You can now easily edit your camp's info in the "My Camp" section below.
            </p>
          </div>
          
          <button
            onClick={handleDismiss}
            className="btn btn-primary w-full"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}


